import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { userService, type OnboardingData } from "@/services/user.service";
import { CheckCircle2, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { BrandName } from "@/components/brand/BrandLogo";

const GRADE_LEVELS = [
  { id: "kindergarten", label: "Kindergarten" },
  { id: "grade-1", label: "Grade 1" },
  { id: "grade-2", label: "Grade 2" },
  { id: "grade-3", label: "Grade 3" },
  { id: "grade-4", label: "Grade 4" },
  { id: "grade-5", label: "Grade 5" },
  { id: "grade-6", label: "Grade 6" },
];

const SUBJECTS = [
  { id: "mathematics", label: "Mathematics" },
  { id: "social-studies", label: "Social Studies" },
  { id: "english-language-arts", label: "English Language Arts" },
  { id: "science", label: "Science" },
];

const TOTAL_STEPS = 4;

export default function Onboarding() {
  const [currentStep, setCurrentStep] = useState(1);
  const [displayName, setDisplayName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [selectedGrades, setSelectedGrades] = useState<string[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const progressValue = (currentStep / TOTAL_STEPS) * 100;

  const handleGradeToggle = (gradeId: string) => {
    setSelectedGrades((prev) =>
      prev.includes(gradeId)
        ? prev.filter((g) => g !== gradeId)
        : [...prev, gradeId]
    );
  };

  const handleSubjectToggle = (subjectId: string) => {
    setSelectedSubjects((prev) =>
      prev.includes(subjectId)
        ? prev.filter((s) => s !== subjectId)
        : [...prev, subjectId]
    );
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return displayName.trim().length > 0;
      case 2:
        return selectedGrades.length > 0;
      case 3:
        return selectedSubjects.length > 0;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleComplete = async () => {
    setError("");
    setLoading(true);

    const controller = new AbortController();

    try {
      const data: OnboardingData = {
        displayName: displayName.trim(),
        schoolName: schoolName.trim() || undefined,
        gradeLevels: selectedGrades,
        subjects: selectedSubjects,
      };

      await userService.completeOnboarding(data, controller.signal);
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        return;
      }
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const getGradeLabel = (id: string) => {
    return GRADE_LEVELS.find((g) => g.id === id)?.label || id;
  };

  const getSubjectLabel = (id: string) => {
    return SUBJECTS.find((s) => s.id === id)?.label || id;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg">
        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Step {currentStep} of {TOTAL_STEPS}</span>
            <span>{Math.round(progressValue)}% complete</span>
          </div>
          <Progress value={progressValue} className="h-2" />
        </div>

        <Card>
          {/* Step 1: Profile */}
          {currentStep === 1 && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl">Welcome to <BrandName /></CardTitle>
                <CardDescription>
                  Let's set up your profile. This helps us personalize your experience.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name *</Label>
                  <Input
                    id="displayName"
                    placeholder="e.g., Ms. Johnson"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">
                    This is how you'll appear in the app
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="schoolName">School Name (optional)</Label>
                  <Input
                    id="schoolName"
                    placeholder="e.g., Westbrook Elementary"
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                  />
                </div>
              </CardContent>
            </>
          )}

          {/* Step 2: Grade Levels */}
          {currentStep === 2 && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl">Grade Levels</CardTitle>
                <CardDescription>
                  Which grade levels do you teach? Select all that apply.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3">
                  {GRADE_LEVELS.map((grade) => (
                    <div
                      key={grade.id}
                      className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent cursor-pointer"
                      onClick={() => handleGradeToggle(grade.id)}
                    >
                      <Checkbox
                        id={grade.id}
                        checked={selectedGrades.includes(grade.id)}
                        onCheckedChange={() => handleGradeToggle(grade.id)}
                      />
                      <Label
                        htmlFor={grade.id}
                        className="flex-1 cursor-pointer font-normal"
                      >
                        {grade.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </>
          )}

          {/* Step 3: Subjects */}
          {currentStep === 3 && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl">Subjects</CardTitle>
                <CardDescription>
                  Which subjects do you teach? Select all that apply.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-3">
                  {SUBJECTS.map((subject) => (
                    <div
                      key={subject.id}
                      className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent cursor-pointer"
                      onClick={() => handleSubjectToggle(subject.id)}
                    >
                      <Checkbox
                        id={subject.id}
                        checked={selectedSubjects.includes(subject.id)}
                        onCheckedChange={() => handleSubjectToggle(subject.id)}
                      />
                      <Label
                        htmlFor={subject.id}
                        className="flex-1 cursor-pointer font-normal"
                      >
                        {subject.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </>
          )}

          {/* Step 4: Confirmation */}
          {currentStep === 4 && (
            <>
              <CardHeader className="space-y-1">
                <CardTitle className="text-2xl">You're All Set!</CardTitle>
                <CardDescription>
                  Review your selections and get started with <BrandName />.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">{displayName}</p>
                      {schoolName && (
                        <p className="text-sm text-muted-foreground">{schoolName}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">Grade Levels</p>
                      <p className="text-sm text-muted-foreground">
                        {selectedGrades.map(getGradeLabel).join(", ")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">Subjects</p>
                      <p className="text-sm text-muted-foreground">
                        {selectedSubjects.map(getSubjectLabel).join(", ")}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </>
          )}

          <CardFooter className="flex justify-between gap-4">
            {currentStep > 1 ? (
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={loading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {currentStep < TOTAL_STEPS ? (
              <Button
                onClick={handleNext}
                disabled={!canProceed()}
              >
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  "Get Started"
                )}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
